import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
} from "ag-grid-community";
import { CustomButton } from "@/components/common/customButton";
import { CustomIconButton } from "@/components/common/customIconButton";
import { ErrorDialog } from "@/components/common/errorDialog";
import { useErrorDialog } from "@/hooks/useErrorDialog";
import {
  downloadReport,
  isDuplicateUploadError,
  isMarketReportApiConfigured,
  listReports,
  uploadReport,
  uploadReportsBatch,
  getJob,
} from "@/services/marketReportApi/marketReportApi";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import type {
  BatchUploadAccepted,
  BatchUploadRejected,
  MarketReportListItem,
  ProcessingJob,
} from "@/types/marketReport";
import { ReportStatusBadge } from "./components/ReportStatusBadge";
import { JobProgressIndicator } from "./components/JobProgressIndicator";
import { RejectedUploadsModal } from "./components/RejectedUploadsModal";
import { formatDate } from "@/utils/formatDate";
import "@/assets/css/AGGrid-custom.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const POLL_INTERVAL_MS = 20000;
/** Stop polling a job after this many consecutive failed status requests. */
export const MAX_CONSECUTIVE_JOB_POLL_FAILURES = 5;
/** Maximum files accepted per POST /reports/upload/batch request. */
export const MAX_BATCH_UPLOAD_SIZE = 20;

/** List row shown in the grid; pending uploads can be overlaid with a "queued" job status. */
type ReportListRow = Omit<MarketReportListItem, "status"> & {
  status: MarketReportListItem["status"] | "queued";
};

type PendingJob = {
  jobId: string;
  reportId: string;
  filename: string;
};

/** Overlay client-side upload/job state onto API list rows while ingestion runs. */
function mergeReportsWithPendingJobs(
  reports: MarketReportListItem[],
  pendingJobs: PendingJob[],
  jobProgress: Record<string, ProcessingJob>,
): ReportListRow[] {
  if (pendingJobs.length === 0) return reports;

  const pendingByReportId = new Map(
    pendingJobs.map((job) => [job.reportId, job]),
  );

  return reports.map((report) => {
    const pending = pendingByReportId.get(report.id);
    if (!pending) return report;

    const job = jobProgress[pending.jobId];
    const isIngesting =
      job === undefined || job.status === "queued" || job.status === "running";

    if (!isIngesting) return report;

    return {
      ...report,
      title: report.title ?? null,
      original_filename: report.original_filename ?? pending.filename,
      status: job?.status === "queued" ? "queued" : "processing",
    };
  });
}

function StatusCellRenderer(params: ICellRendererParams<ReportListRow>) {
  if (!params.data) return null;
  return <ReportStatusBadge status={params.data.status} compact />;
}

type DownloadCellRendererParams = ICellRendererParams<ReportListRow> & {
  onDownload: (report: ReportListRow) => void;
  downloadingId: string | null;
};

function DownloadCellRenderer(params: DownloadCellRendererParams) {
  const report = params.data;
  if (!report) return null;

  const isDownloading = params.downloadingId === report.id;

  return (
    <Box
      data-market-report-download="true"
      sx={{ display: "flex", alignItems: "center", height: "100%" }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <CustomIconButton
        tooltip="Download PDF"
        disabled={isDownloading}
        icon={
          isDownloading ? (
            <CircularProgress size={16} />
          ) : (
            <Box
              component="img"
              src={downloadIcon}
              alt="Download report"
              sx={{ width: 20, height: 20, display: "block" }}
            />
          )
        }
        iconButtonSx={{ p: 0.4 }}
        onClick={() => params.onDownload(report)}
      />
    </Box>
  );
}

export function ReportsTab() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );
  const reportsPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const [reports, setReports] = useState<MarketReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [jobProgress, setJobProgress] = useState<Record<string, ProcessingJob>>(
    {},
  );
  const [duplicateAlert, setDuplicateAlert] = useState<{
    reportId: string;
    message: string;
  } | null>(null);
  const [rejectedUploads, setRejectedUploads] = useState<BatchUploadRejected[]>(
    [],
  );
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  /** Filenames whose job status could no longer be retrieved (polling gave up). */
  const [untrackedJobFiles, setUntrackedJobFiles] = useState<string[]>([]);
  const { errorDialog, showError, hideError } = useErrorDialog();

  const closeRejectedModal = useCallback(() => {
    setRejectedModalOpen(false);
    setRejectedUploads([]);
  }, []);

  const handleViewRejectedReport = useCallback(
    (reportId: string) => {
      closeRejectedModal();
      navigate(`/market-reports/reports/${reportId}`);
    },
    [closeRejectedModal, navigate],
  );

  const fetchReports = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!isMarketReportApiConfigured()) {
        setLoading(false);
        return;
      }
      if (!options?.silent) {
        setLoading(true);
      }
      const response = await listReports();
      if (!options?.silent) {
        setLoading(false);
      }
      if (response.success && response.data) {
        setReports(response.data);
      } else if (!options?.silent) {
        showError(
          undefined,
          typeof response.message === "string"
            ? response.message
            : "Failed to load reports",
        );
      }
    },
    [showError],
  );

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  const stopPolling = useCallback((jobId: string) => {
    const timer = pollTimersRef.current.get(jobId);
    if (timer) {
      clearInterval(timer);
      pollTimersRef.current.delete(jobId);
    }
  }, []);

  const stopReportsPoll = useCallback(() => {
    if (reportsPollTimerRef.current) {
      console.log("[ReportsTab] stopReportsPoll: clearing reports poll interval");
      clearInterval(reportsPollTimerRef.current);
      reportsPollTimerRef.current = null;
    }
  }, []);

  const pollReports = useCallback(() => {
    console.log("[ReportsTab] pollReports: starting reports poll");
    stopReportsPoll();
    console.log("[ReportsTab] pollReports: initial fetchReports");
    void fetchReports();
    reportsPollTimerRef.current = setInterval(() => {
      console.log("[ReportsTab] pollReports: interval fetchReports");
      void fetchReports({ silent: true });
    }, POLL_INTERVAL_MS);
  }, [fetchReports, stopReportsPoll]);

  const startJobPoll = useCallback(
    (job: PendingJob, onAfterPoll?: () => void) => {
      console.log("[ReportsTab] startJobPoll: starting", {
        jobId: job.jobId,
        reportId: job.reportId,
        withRefresh: Boolean(onAfterPoll),
      });

      let consecutiveFailures = 0;

      const poll = async () => {
        console.log("[ReportsTab] startJobPoll: poll tick", {
          jobId: job.jobId,
        });
        let response: Awaited<ReturnType<typeof getJob>> | null = null;
        try {
          response = await getJob(job.jobId);
        } catch (error) {
          console.warn("[ReportsTab] startJobPoll: poll request threw", {
            jobId: job.jobId,
            error,
          });
        }
        if (!response?.success || !response.data) {
          consecutiveFailures += 1;
          console.log("[ReportsTab] startJobPoll: poll failed or empty", {
            jobId: job.jobId,
            success: response?.success,
            status: response?.status,
            consecutiveFailures,
          });
          // A missing job (404) will never recover; otherwise give up after
          // repeated failures so the job (and the reports poll) can't run forever.
          if (
            response?.status === 404 ||
            consecutiveFailures >= MAX_CONSECUTIVE_JOB_POLL_FAILURES
          ) {
            stopPolling(job.jobId);
            setPendingJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
            setUntrackedJobFiles((prev) =>
              prev.includes(job.filename) ? prev : [...prev, job.filename],
            );
          }
          return;
        }
        consecutiveFailures = 0;

        console.log("[ReportsTab] startJobPoll: job status", {
          jobId: job.jobId,
          status: response.data.status,
        });

        setJobProgress((prev) => ({
          ...prev,
          [job.jobId]: response.data!,
        }));

        if (
          response.data.status === "completed" ||
          response.data.status === "failed"
        ) {
          console.log("[ReportsTab] startJobPoll: job finished, stopping", {
            jobId: job.jobId,
            status: response.data.status,
          });
          stopPolling(job.jobId);
          setPendingJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
        }

        onAfterPoll?.();
      };

      void poll();
      const timer = setInterval(() => void poll(), POLL_INTERVAL_MS);
      pollTimersRef.current.set(job.jobId, timer);
    },
    [stopPolling],
  );

  const pollJobProgress = useCallback(
    (job: PendingJob) => {
      console.log("[ReportsTab] pollJobProgress", {
        jobId: job.jobId,
        reportId: job.reportId,
      });
      startJobPoll(job);
    },
    [startJobPoll],
  );

  const pollJob = useCallback(
    (job: PendingJob) => {
      console.log("[ReportsTab] pollJob", {
        jobId: job.jobId,
        reportId: job.reportId,
      });
      startJobPoll(job, () => {
        console.log("[ReportsTab] pollJob: onAfterPoll fetchReports", {
          jobId: job.jobId,
        });
        void fetchReports({ silent: true });
      });
    },
    [fetchReports, startJobPoll],
  );

  useEffect(() => {
    if (pendingJobs.length === 0) {
      console.log(
        "[ReportsTab] pendingJobs empty: stopping reports poll if active",
      );
      stopReportsPoll();
    }
  }, [pendingJobs.length, stopReportsPoll]);

  useEffect(() => {
    return () => {
      console.log("[ReportsTab] unmount: clearing all poll timers");
      for (const timer of pollTimersRef.current.values()) {
        clearInterval(timer);
      }
      pollTimersRef.current.clear();
      if (reportsPollTimerRef.current) {
        clearInterval(reportsPollTimerRef.current);
        reportsPollTimerRef.current = null;
      }
    };
  }, []);

  const handleDownload = useCallback(
    async (report: ReportListRow) => {
      setDownloadingId(report.id);
      const response = await downloadReport(report.id);
      setDownloadingId(null);

      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data.blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = response.data.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
        return;
      }

      showError(
        undefined,
        typeof response.message === "string"
          ? response.message
          : "Failed to download report",
      );
    },
    [showError],
  );

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;

    setUploading(true);
    setDuplicateAlert(null);
    setRejectedModalOpen(false);
    setRejectedUploads([]);

    if (files.length === 1) {
      const response = await uploadReport(files[0]);
      setUploading(false);

      if (response.success && response.data) {
        const pending: PendingJob = {
          jobId: response.data.job_id,
          reportId: response.data.report_id,
          filename: files[0].name,
        };
        setPendingJobs((prev) => [...prev, pending]);
        pollJob(pending);
        void fetchReports();
      } else if (isDuplicateUploadError(response.message)) {
        setDuplicateAlert({
          reportId: response.message.report_id,
          message: response.message.message,
        });
      } else {
        showError(
          undefined,
          typeof response.message === "string"
            ? response.message
            : "Upload failed",
        );
      }
      return;
    }

    // The batch endpoint accepts up to MAX_BATCH_UPLOAD_SIZE files per
    // request, so upload larger selections in sequential chunks.
    const accepted: BatchUploadAccepted[] = [];
    const rejected: BatchUploadRejected[] = [];
    let succeededChunks = 0;
    let lastErrorMessage = "Batch upload failed";

    for (let start = 0; start < files.length; start += MAX_BATCH_UPLOAD_SIZE) {
      const chunk = files.slice(start, start + MAX_BATCH_UPLOAD_SIZE);
      let errorMessage = "Batch upload failed";
      try {
        const response = await uploadReportsBatch(chunk);
        if (response.success && response.data) {
          succeededChunks += 1;
          accepted.push(...response.data.accepted);
          rejected.push(...response.data.rejected);
          continue;
        }
        if (typeof response.message === "string") {
          errorMessage = response.message;
        }
      } catch (error) {
        console.warn("[ReportsTab] batch upload chunk threw", error);
      }
      lastErrorMessage = errorMessage;
      rejected.push(
        ...chunk.map((file) => ({ filename: file.name, error: errorMessage })),
      );
    }
    setUploading(false);

    if (succeededChunks === 0) {
      showError(undefined, lastErrorMessage);
      return;
    }

    const newPending: PendingJob[] = accepted.map((item) => ({
      jobId: item.job_id,
      reportId: item.report_id,
      filename: item.filename,
    }));
    if (newPending.length > 0) {
      setPendingJobs((prev) => [...prev, ...newPending]);
      console.log("[ReportsTab] batch upload accepted", {
        count: newPending.length,
        jobIds: newPending.map((job) => job.jobId),
      });
      for (const job of newPending) {
        pollJobProgress(job);
      }
      pollReports();
    }

    if (rejected.length > 0) {
      setRejectedUploads(rejected);
      setRejectedModalOpen(true);
    }
  };

  const columnDefs = useMemo<ColDef<ReportListRow>[]>(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 200,
        valueGetter: (params) =>
          params.data?.title ||
          params.data?.original_filename ||
          params.data?.id ||
          "—",
      },
      {
        field: "publisher",
        headerName: "Publisher",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => params.value ?? "—",
      },
      {
        field: "publication_date",
        headerName: "Published",
        width: 120,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        cellRenderer: StatusCellRenderer,
      },
      {
        field: "created_at",
        headerName: "Uploaded",
        width: 120,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        colId: "download",
        headerName: "",
        width: 52,
        maxWidth: 52,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: DownloadCellRenderer,
        cellRendererParams: {
          onDownload: handleDownload,
          downloadingId,
        },
      },
    ],
    [downloadingId, handleDownload],
  );

  const displayReports = useMemo(
    () => mergeReportsWithPendingJobs(reports, pendingJobs, jobProgress),
    [reports, pendingJobs, jobProgress],
  );

  return (
    <Box className="market-reports-tab-body">
      <div className="market-reports-toolbar">
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 500 }}>
          Report library
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomButton
            text="Refresh"
            handleButtonPress={() => void fetchReports()}
            isButtonDisabled={loading}
          />
          <CustomButton
            text={uploading ? "Uploading…" : "Upload PDFs"}
            handleButtonPress={() => fileInputRef.current?.click()}
            isButtonDisabled={uploading}
            isLoading={uploading}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            hidden
            onChange={(e) => void handleFilesSelected(e)}
          />
        </Box>
      </div>

      {duplicateAlert ? (
        <Alert
          severity="info"
          sx={{ mb: 2, flexShrink: 0 }}
          onClose={() => setDuplicateAlert(null)}
        >
          {duplicateAlert.message}.{" "}
          <Link
            component="button"
            underline="hover"
            onClick={() =>
              navigate(`/market-reports/reports/${duplicateAlert.reportId}`)
            }
          >
            View existing report
          </Link>
        </Alert>
      ) : null}

      {untrackedJobFiles.length > 0 ? (
        <Alert
          severity="warning"
          sx={{ mb: 2, flexShrink: 0 }}
          onClose={() => setUntrackedJobFiles([])}
        >
          Stopped tracking processing for {untrackedJobFiles.join(", ")}: the
          job status could not be retrieved. The report may still be
          processing; use Refresh to check its status later.
        </Alert>
      ) : null}

      {pendingJobs.length > 0 ? (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {pendingJobs.map((job) => {
            const progress = jobProgress[job.jobId];
            return (
              <Box
                key={job.jobId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  bgcolor: "#f5fbff",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 160 }}>
                  {job.filename}
                </Typography>
                <JobProgressIndicator
                  stage={progress?.current_stage}
                  jobStatus={progress?.status}
                />
              </Box>
            );
          })}
        </Box>
      ) : null}

      <Box className="market-reports-grid-wrap">
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div
            className="ag-theme-alpine market-reports-grid"
            style={{ width: "100%", height: "100%" }}
          >
            <AgGridReact
              rowData={displayReports}
              columnDefs={columnDefs}
              defaultColDef={{ sortable: true, resizable: true }}
              rowHeight={30}
              headerHeight={36}
              suppressRowClickSelection
              onCellClicked={(event) => {
                const colId = event.column?.getColId?.() ?? event.colDef?.colId;
                const target = event.event?.target as HTMLElement | null;
                if (
                  colId === "download" ||
                  target?.closest("[data-market-report-download]")
                ) {
                  return;
                }
                if (event.data?.id) {
                  navigate(`/market-reports/reports/${event.data.id}`);
                }
              }}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        )}
      </Box>

      <RejectedUploadsModal
        open={rejectedModalOpen}
        rejected={rejectedUploads}
        onClose={closeRejectedModal}
        onViewReport={handleViewRejectedReport}
      />

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
